import { Button, Col, Input, Row, Table, Tag } from "antd";
import moment from "moment";
import { debounce } from "lodash";
import React, { useMemo, useState } from "react";
import { FaPencilAlt, FaPlus, FaRegTrashAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useApi } from "../../../../../hooks/http2";
import { axios, getToken, URL_API } from "../../../../../utils";
import { AlertConfirm, AlertError, AlertSuccess } from "../../../../util/Alert";
import { Panel, PanelBody, PanelHeader } from "../../../../util/panel";

const PROCESS_STATUS_LABEL = {
  PENDING: { text: "รอดำเนินการ", color: "gold" },
  PROCESSED: { text: "ประมวลผลแล้ว", color: "blue" },
  SUCCESS: { text: "สำเร็จ", color: "green" },
  FAILED: { text: "ล้มเหลว", color: "red" },
};

export default function KnowledgeSourceList({ history, match }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;

  const debounceSearch = useMemo(
    () =>
      debounce((value) => {
        setPage(1);
        setSearch(value);
      }, 500),
    []
  );

  const list = useApi(
    {
      baseURL: URL_API,
      url: "/ai/product/knowledge-source",
      params: {
        search,
        limit,
        offset: (page - 1) * limit,
      },
      token: true,
    },
    [search, page]
  );

  const handleDelete = async (id) => {
    const confirm = await AlertConfirm("ลบข้อมูล");
    if (!confirm) return;

    axios({
      method: "delete",
      baseURL: URL_API,
      url: `/ai/product/knowledge-source/${id}`,
      headers: { Authorization: "Bearer " + getToken() },
    })
      .then(async () => {
        await AlertSuccess("ลบข้อมูลสำเร็จ");
        history.go(0);
      })
      .catch((err) => {
        AlertError({
          text: err && err.response ? err.response.data.message : err.message,
        });
      });
  };

  return (
    <Panel>
      <PanelHeader>ข้อมูลเสริมสินค้าสำหรับ AI</PanelHeader>
      <PanelBody>
        <Row type="flex" justify="space-between" gutter={16} className="mb-4">
          <Col span={8}>
            <Input.Search
              allowClear
              placeholder="ค้นหาด้วยรหัสสินค้า ชื่อสินค้า"
              onChange={(e) => debounceSearch(e.target.value)}
              onSearch={(value) => {
                setPage(1);
                setSearch(value);
              }}
            />
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={() => history.push(`${match.path}/add`)}
            >
              <FaPlus className="mr-2" />
              เพิ่มข้อมูล
            </Button>
          </Col>
        </Row>
        <Table
          bordered
          size="small"
          loading={list?.loading}
          dataSource={list?.fetch?.list || []}
          rowKey="id"
          pagination={{
            size: "default",
            showLessItems: true,
            showTotal: (total, range) => `${range[0]} - ${range[1]} of ${total}`,
            total: list?.fetch?.total || 0,
            current: page,
            pageSize: limit,
            onChange: (nextPage) => setPage(nextPage),
          }}
        >
          <Table.Column
            title="รหัสสินค้า"
            key="product_code"
            dataIndex="product_code"
            align="center"
          />
          <Table.Column
            title="ชื่อสินค้า"
            key="product_name"
            dataIndex="product_name"
            align="center"
          />
          <Table.Column
            title="ประเภท"
            key="source_type"
            dataIndex="source_type"
            align="center"
            render={(text) => (text === "pdf" ? "ไฟล์ PDF" : "ข้อความ")}
          />
          <Table.Column
            title="ไฟล์"
            key="file_name"
            dataIndex="file_name"
            align="center"
          />
          <Table.Column
            title="สถานะ"
            key="process_status"
            dataIndex="process_status"
            align="center"
            render={(text) => (
              <Tag color={PROCESS_STATUS_LABEL[text]?.color}>
                {PROCESS_STATUS_LABEL[text]?.text || text}
              </Tag>
            )}
          />
          <Table.Column
            title="วันที่สร้าง"
            key="create_date"
            dataIndex="create_date"
            align="center"
            render={(text) => (text ? moment(text).format("DD/MM/YYYY HH:mm") : "")}
          />
          <Table.Column
            title="จัดการ"
            key="manage"
            align="center"
            render={(text, { id }) => (
              <Row>
                <Link to={`${match.path}/${id}`}>
                  <Button type="primary">
                    <FaPencilAlt />
                  </Button>
                </Link>
                <Button
                  className="btn-danger ml-1"
                  onClick={() => handleDelete(id)}
                >
                  <FaRegTrashAlt />
                </Button>
              </Row>
            )}
          />
        </Table>
      </PanelBody>
    </Panel>
  );
}
